import { Link } from '@tanstack/react-router'
import { motion, type Transition } from 'motion/react'

import SSRIcon from '@ingka/ssr-icon'
import arrowRight from '@ingka/ssr-icon/paths/arrow-right'
import Text from '@ingka/text'

import { ImageWithAspectRatio } from '@/components/image-with-aspect-ratio'
import { MissingBadge } from '@/components/missing-badge'
import { useInsights } from '@/hooks/use-insights'
import { useTranslate } from '@/hooks/use-translate'

import { RangeFeatures } from './components/range-features'
import { useRangeGalleryData } from './hooks/use-range-gallery-data'
import { breakWord, card, iconContainer, image, text } from './start-gallery.module.css'

const spring: Transition = {
  bounce: 0.01,
  duration: 0.3,
  type: 'spring',
}
export function StartGallery() {
  const data = useRangeGalleryData()
  const { translateString } = useTranslate()
  const { sendCustomEvent } = useInsights()

  if (data.length === 0) {
    return (
      <Text className={text} tagName="strong" textStyle="Heading.L">
        {translateString('startpage.filter.no_result')}
      </Text>
    )
  }

  return (
    <>
      {data.map((rangeData, index) => (
        <motion.li
          data-has-armrests={rangeData.hasArmrests}
          data-has-legs={rangeData.hasLegs}
          data-has-mattresses={rangeData.hasMattresses}
          data-testid={`range-${rangeData.rangeId}`}
          key={rangeData.rangeId}
          layout
          transition={spring}
        >
          <Link
            className={card}
            from="/$country/$language/navigator/"
            key={rangeData.name}
            onClick={() => {
              void sendCustomEvent('select_gallery_sofa', {
                payload: { index, itemNo: rangeData.posterSofa.itemNo, rangeId: rangeData.rangeId },
              })
            }}
            params={{ id: rangeData.posterSofa.itemNo, rangeId: rangeData.rangeId }}
            resetScroll={false}
            search={(prev) => ({ hasPriceDisclaimerBeenShown: prev.hasPriceDisclaimerBeenShown })}
            state={{ fromGallery: true }}
            to="/$country/$language/navigator/$rangeId/$id"
            viewTransition={{ types: ['slide-left'] }}
          >
            {rangeData.missingSofas && (
              <MissingBadge
                label={
                  rangeData.missingSofas === 'every'
                    ? translateString('common.missing_every')
                    : translateString('common.missing_some')
                }
                severity={rangeData.missingSofas === 'every' ? 'error' : 'info'}
              />
            )}
            <Text className={breakWord} tagName="h2" textStyle="Heading.M">
              {rangeData.posterSofa.productData.content.name}
            </Text>
            <ImageWithAspectRatio
              alt={rangeData.posterImg.alt}
              aspectRatio="square"
              className={image}
              fetchPriority="high"
              height={200}
              loading="eager"
              src={rangeData.posterImg.src}
              width={200}
            />
            <RangeFeatures rangeGalleryData={rangeData} />
            <div className={iconContainer}>
              <SSRIcon paths={arrowRight} />
            </div>
          </Link>
        </motion.li>
      ))}
    </>
  )
}
